// mixins/tabRecordingMixin.js
export default {
    data() {
        return {
            // Recording state
            mediaRecorder: null,
            recordedChunks: [],
            recordingStartTime: null,
            recordingDuration: 0,
            recordingStream: null,
            isRecording: false,
            isPreparingRecording: false,
            showRecordingControls: false,
            recordingError: null,

            // Force hide all UI during recording
            forceHideAllUI: false,

            // Stream pending countdown completion
            pendingStream: null,

            // Quality settings - optimized for best audio/video quality
            recordingQuality: {
                video: {
                    width: { ideal: 1920, max: 3840 },
                    height: { ideal: 1080, max: 2160 },
                    frameRate: { ideal: 60, max: 60 },
                    displaySurface: 'browser', // Only capture browser tab
                },
                audio: {
                    echoCancellation: false, // Disable for better quality
                    noiseSuppression: false, // Disable for better quality
                    autoGainControl: false, // Disable for better quality
                    sampleRate: 48000, // High sample rate
                    sampleSize: 16, // 16-bit audio
                    channelCount: 2, // Stereo
                    latency: 0, // Lowest latency
                    volume: 1.0, // Full volume
                }
            },

            // Recording formats and codecs - optimized for quality
            recordingOptions: {
                mimeType: null, // Will be set based on browser support
                videoBitsPerSecond: 12000000, // 12 Mbps for high quality
                audioBitsPerSecond: 384000, // 384 kbps for studio quality audio
            },

            // Supported codecs in order of preference - added more high-quality options
            supportedCodecs: [
                { mimeType: 'video/webm;codecs=vp9,opus', extension: 'webm' },
                { mimeType: 'video/webm;codecs=vp8,opus', extension: 'webm' },
                { mimeType: 'video/webm;codecs=h264,opus', extension: 'webm' },
                { mimeType: 'video/webm', extension: 'webm' },
                { mimeType: 'video/mp4;codecs=h264,aac', extension: 'mp4' },
                { mimeType: 'video/mp4', extension: 'mp4' }
            ]
        };
    },

    computed: {
        formattedRecordingDuration() {
            const minutes = Math.floor(this.recordingDuration / 60);
            const seconds = this.recordingDuration % 60;
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
    },

    methods: {
        // Initialize recording capabilities
        async initializeRecording() {
            // Check if browser supports screen recording
            if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
                this.recordingError = 'Your browser does not support screen recording';
                return false;
            }

            // Find the best supported codec
            for (const codec of this.supportedCodecs) {
                if (MediaRecorder.isTypeSupported(codec.mimeType)) {
                    this.recordingOptions.mimeType = codec.mimeType;
                    this.recordingExtension = codec.extension;
                    console.log(`Using codec: ${codec.mimeType}`);
                    break;
                }
            }

            if (!this.recordingOptions.mimeType) {
                this.recordingError = 'No supported video codec found';
                return false;
            }

            return true;
        },

        // Start recording process (kept for compatibility - now uses countdown in the component)
        async startRecording() {
            // This method is now primarily handled by startRecordingWithCountdown in the component
            if (this.startRecordingWithCountdown) {
                this.startRecordingWithCountdown();
            } else {
                console.warn('startRecordingWithCountdown method not found in component');
            }
        },

        // Stop recording
        async stopRecording() {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                return;
            }

            // Stop the MediaRecorder
            this.mediaRecorder.stop();
            this.isRecording = false;

            // Stop all tracks
            if (this.recordingStream) {
                this.recordingStream.getTracks().forEach(track => track.stop());
            }

            // Stop playback
            if (this.isPlaying) {
                this.togglePlay();
            }

            // Exit fullscreen
            if (this.isFullscreen) {
                this.exitFullscreen();
            }

            // Show controls again
            this.showControls = true;
            this.forceHideAllUI = false;
        },

        // Save recording to file
        saveRecording() {
            if (this.recordedChunks.length === 0) {
                this.recordingError = 'No recording data available';
                return;
            }

            // Create blob from recorded chunks
            const blob = new Blob(this.recordedChunks, {
                type: this.recordingOptions.mimeType
            });

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `screen-recording-${timestamp}.${this.recordingExtension}`;

            // Create download link
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 100);

            // Emit event for parent component
            this.$emit('recording-saved', {
                filename,
                size: this.formatBytes(blob.size),
                duration: this.formattedRecordingDuration
            });
        },

        // Clean up resources
        cleanup() {
            if (this.recordingStream) {
                this.recordingStream.getTracks().forEach(track => track.stop());
                this.recordingStream = null;
            }

            if (this.pendingStream) {
                this.pendingStream.getTracks().forEach(track => track.stop());
                this.pendingStream = null;
            }

            this.mediaRecorder = null;
            this.recordedChunks = [];
            this.recordingStartTime = null;
            this.recordingDuration = 0;
            this.stopRecordingTimer();
        },

        // Timer for recording duration
        startRecordingTimer() {
            this.recordingTimer = setInterval(() => {
                this.recordingDuration = Math.floor((Date.now() - this.recordingStartTime) / 1000);
            }, 1000);
        },

        stopRecordingTimer() {
            if (this.recordingTimer) {
                clearInterval(this.recordingTimer);
                this.recordingTimer = null;
            }
        },

        // Handle keyboard shortcuts for recording
        handleRecordingKeyDown(e) {
            // Only handle recording shortcuts if we're in recording mode
            if (!this.isRecording) return;

            switch (e.key) {
                case 'Escape':
                case 'q':
                case 'Q':
                    // Stop recording with Escape or Q
                    e.preventDefault();
                    this.stopRecording();
                    break;
                case 'p':
                case 'P':
                    // Toggle pause/resume with P (if supported)
                    e.preventDefault();
                    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
                        this.mediaRecorder.pause();
                    } else if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
                        this.mediaRecorder.resume();
                    }
                    break;
            }
        },

        // Toggle recording mode
        toggleRecordingMode() {
            if (this.isRecording) {
                this.stopRecording();
            } else {
                this.startRecording();
            }
        },

        // Utility function to format bytes
        formatBytes(bytes, decimals = 2) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const dm = decimals < 0 ? 0 : decimals;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
        }
    },

    mounted() {
        // Add recording keyboard shortcuts
        window.addEventListener('keydown', this.handleRecordingKeyDown);
    },

    beforeDestroy() {
        // Clean up event listeners and resources
        window.removeEventListener('keydown', this.handleRecordingKeyDown);
        this.cleanup();
    }
};
